/**
 * HealthTab — aba "Saude ZTNA" no dashboard admin.
 *
 * Exibe alertas de drift da tabela ztna_drift_alerts via
 * GET /admin/ztna/drift-alerts. Refetch automatico a cada 30s.
 *
 * Funcionalidades:
 * - Header com contagem de alertas abertos + resolvidos
 * - Filtro rapido por status (open / resolved / all)
 * - Tabela com colunas: tipo (badge com cor por severidade),
 *   detectado_em, mensagem de erro, link de recurso
 * - Estado vazio amigavel quando nao ha alertas
 *
 * Spec: docs/superpowers/plans/2026-05-10-ztna-resilience-gap4-drift-monitor.md Task 4
 */
import { useState } from 'react'
import {
  Alert,
  Badge,
  Box,
  Group,
  Loader,
  SegmentedControl,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from '@mantine/core'
import { IconAlertTriangle, IconCheck, IconHeartbeat } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { ztnaApi } from '../api'
import type { DriftAlert } from '../types'

// ─────────────────────────────────────────────────────────────────
// Badge color by drift_type
// ─────────────────────────────────────────────────────────────────

const DRIFT_BADGE_COLOR: Record<string, string> = {
  outbox_stuck: 'yellow',
  job_stale: 'orange',
  smoke_not_intercepted: 'red',
}

function driftBadgeColor(driftType: string): string {
  if (driftType in DRIFT_BADGE_COLOR) return DRIFT_BADGE_COLOR[driftType]
  if (driftType.startsWith('cert_')) return 'red'
  return 'gray'
}

// ─────────────────────────────────────────────────────────────────
// Drift type labels
// ─────────────────────────────────────────────────────────────────

const DRIFT_LABEL: Record<string, string> = {
  outbox_stuck: 'Outbox stuck',
  job_stale: 'Job stale',
  smoke_not_intercepted: 'Smoke não interceptado',
  cert_pending: 'Cert pendente',
  cert_expiring: 'Cert expirando',
}

function driftLabel(driftType: string): string {
  return DRIFT_LABEL[driftType] ?? driftType
}

// ─────────────────────────────────────────────────────────────────
// Date formatting
// ─────────────────────────────────────────────────────────────────

function formatDateTime(iso: string): string {
  try {
    const d = new Date(iso)
    return d.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return iso
  }
}

// ─────────────────────────────────────────────────────────────────
// Row component
// ─────────────────────────────────────────────────────────────────

function DriftAlertRow({ alert }: { alert: DriftAlert }) {
  const color = driftBadgeColor(alert.drift_type)
  const label = driftLabel(alert.drift_type)
  const isResolved = alert.resolved_at != null

  return (
    <Table.Tr style={{ opacity: isResolved ? 0.6 : 1 }}>
      <Table.Td>
        <Badge color={color} variant="light" size="sm" radius="sm">
          {label}
        </Badge>
      </Table.Td>
      <Table.Td>
        <Text size="xs" c="dimmed">
          {formatDateTime(alert.detected_at)}
        </Text>
      </Table.Td>
      <Table.Td>
        {isResolved ? (
          <Text size="xs" c="dimmed">
            {formatDateTime(alert.resolved_at!)}
          </Text>
        ) : (
          <Text size="xs" c="dimmed">
            —
          </Text>
        )}
      </Table.Td>
      <Table.Td>
        <Tooltip label={alert.error ?? ''} multiline maw={420} withArrow disabled={!alert.error}>
          <Text
            size="xs"
            lineClamp={2}
            style={{ maxWidth: 340, cursor: alert.error ? 'help' : undefined }}
          >
            {alert.error ?? '—'}
          </Text>
        </Tooltip>
      </Table.Td>
      <Table.Td>
        <Text size="xs" c="dimmed">
          {alert.resource_type ?? '—'}
          {alert.resource_arn ? ` #${alert.resource_arn}` : ''}
        </Text>
      </Table.Td>
    </Table.Tr>
  )
}

// ─────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────

type StatusFilter = 'open' | 'resolved' | 'all'

export function HealthTab() {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('open')

  const alertsQuery = useQuery({
    queryKey: ['ztna', 'drift-alerts', statusFilter],
    queryFn: () => ztnaApi.listDriftAlerts(statusFilter, 100),
    refetchInterval: 30_000,
    staleTime: 25_000,
  })

  const data = alertsQuery.data
  const isLoading = alertsQuery.isLoading
  const isError = alertsQuery.isError

  const openCount = data?.open_count ?? 0
  const resolvedCount = data?.resolved_count ?? 0
  const items = data?.items ?? []

  return (
    <Stack gap="md">
      {/* Header */}
      <Group justify="space-between" align="flex-start">
        <div>
          <Group gap="xs" align="center">
            <IconHeartbeat size={20} color="var(--ai-brand-azul)" />
            <Title order={4} c="var(--ai-brand-azul)">
              Saúde ZTNA
            </Title>
          </Group>
          <Text size="sm" c="dimmed" mt={2}>
            Alertas de drift detectados pelo monitor automático (atualiza a cada 30s).
          </Text>
        </div>

        <Group gap="md">
          <Box>
            <Text size="xs" c="dimmed" ta="center">
              Abertos
            </Text>
            <Text fw={700} size="lg" c={openCount > 0 ? 'orange' : 'green'} ta="center">
              {openCount}
            </Text>
          </Box>
          <Box>
            <Text size="xs" c="dimmed" ta="center">
              Resolvidos
            </Text>
            <Text fw={700} size="lg" c="dimmed" ta="center">
              {resolvedCount}
            </Text>
          </Box>
        </Group>
      </Group>

      {/* Filter */}
      <Group>
        <SegmentedControl
          size="xs"
          value={statusFilter}
          onChange={(v) => setStatusFilter(v as StatusFilter)}
          data={[
            { label: 'Abertos', value: 'open' },
            { label: 'Resolvidos', value: 'resolved' },
            { label: 'Todos', value: 'all' },
          ]}
        />
        {isLoading && <Loader size="xs" />}
      </Group>

      {/* Error state */}
      {isError && (
        <Alert
          color="red"
          variant="light"
          icon={<IconAlertTriangle size={16} />}
          title="Falha ao carregar alertas"
        >
          Verifique permissões (ztna:onboard:provision) ou tente recarregar.
        </Alert>
      )}

      {/* Empty state */}
      {!isLoading && !isError && items.length === 0 && (
        <Stack align="center" gap="xs" py="xl">
          <IconCheck size={40} color="var(--mantine-color-green-6)" />
          <Text size="sm" c="dimmed">
            {statusFilter === 'open'
              ? 'Nenhum alerta de drift aberto'
              : statusFilter === 'resolved'
              ? 'Nenhum alerta resolvido'
              : 'Nenhum alerta registrado'}
          </Text>
        </Stack>
      )}

      {/* Table */}
      {items.length > 0 && (
        <Table striped highlightOnHover withTableBorder withColumnBorders verticalSpacing="xs">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Tipo de drift</Table.Th>
              <Table.Th>Detectado em</Table.Th>
              <Table.Th>Resolvido em</Table.Th>
              <Table.Th>Mensagem</Table.Th>
              <Table.Th>Recurso</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {items.map((alert) => (
              <DriftAlertRow key={alert.id} alert={alert} />
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Stack>
  )
}

export default HealthTab
